import { Box } from '@apia/theme';
import { useBoolean, useUpdateEffect } from 'ahooks';
import React from 'react';
import ConfirmModal from '../../../common/dialogs/modals/ConfirmModal';
import { TApiaComponent, TApiaForm } from '../../../../types';
import { useAppSelector } from '../../../../hooks/hooks';
import persistentStorage from '../../../../utils/persistentStorage';
import CollapsiblePanel from '../../../common/CollapsiblePanel';
import JSONAccordion from './JSONAccordion';
import fieldsStore from '../fieldsStore/fieldsStore';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import { debugSizeEmitter } from '../../../common/DebugInfo';

export interface IApiaDebugInfo {
  id: string;
  data?: TApiaComponent | TApiaForm;
}

const ApiaDebugInfo = ({ id, data }: IApiaDebugInfo) => {
  const [isToggled, { setTrue, setFalse }] = useBoolean(false);
  const props = usePropsSelector(id);
  const gridProps = useAppSelector((global) => global.dataGridSlice[id]);
  const [size, setSize] = React.useState(0);

  React.useEffect(() => {
    const storedSize = persistentStorage.debugSize;
    if (storedSize !== undefined && storedSize !== size)
      setSize(storedSize as number);

    const unregister = debugSizeEmitter.on('debugSize', setSize);

    return () => {
      unregister();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useUpdateEffect(() => {
    persistentStorage.debugSize = size;
  }, [size]);

  const name = `${id} | ${props?.name || ''}`;
  const attId = `${props?.attributes?.attId || ''}`;

  // const keys = Array.from(id) as Hotkey[];

  const getDebugData = (): JSX.Element => {
    return (
      <main
        sx={{
          '*': {
            resize: 'both',
          },
          '.collapsiblePanel__label': {
            fontWeight: 'bold',
            width: '100%',
          },
          '.collapsiblePanel:hover': {
            '& > .collapsiblePanel__label': {
              bg: 'rgba(0,0,0,0.1)',
            },
          },
        }}
      >
        <Box>
          <Box as="strong">Value:</Box>
          {JSON.stringify(fieldsStore.getById(id)?.getValue())}
        </Box>
        {gridProps && (
          <CollapsiblePanel collapsed id="GridStore" label="Grid props">
            <JSONAccordion obj={gridProps} />
          </CollapsiblePanel>
        )}
        <CollapsiblePanel collapsed id="PropsStore" label="Props store">
          <JSONAccordion obj={props} />
        </CollapsiblePanel>
        <CollapsiblePanel collapsed id="DataXML" label="Data-XML">
          <JSONAccordion obj={data as unknown as Record<string, unknown>} />
        </CollapsiblePanel>
      </main>
    );
  };

  if (size === 0) return null;

  return (
    <Box>
      <Box
        role="button"
        className="debug_info__button"
        tabIndex={-1}
        onClick={setTrue}
        onKeyDown={() => {}}
        sx={{
          bg: 'blue',
          width: size,
          height: size,
          cursor: 'pointer',
        }}
      />
      <ConfirmModal
        isOpen={isToggled}
        onExited={setFalse}
        size="xxxl"
        sx={{
          '.modal__title': {
            userSelect: 'text',
          },
        }}
        title={`${name} | ${attId}`}
      >
        {getDebugData()}
      </ConfirmModal>
    </Box>
  );
};

export default ApiaDebugInfo;
